"use client";

import { useMemo } from "react";
import { calculateKernelDensity } from "@/lib/simulation";
import { useI18n } from "@/lib/i18n/hook";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface HistogramChartProps {
  data: number[];
  kernelDensity?: {
    x: number[];
    y: number[];
  };
  targetAmount?: number;
}

export default function HistogramChart({
  data,
  kernelDensity,
  targetAmount,
}: HistogramChartProps) {
  const { t } = useI18n();

  const chartData = useMemo(() => {
    if (data.length === 0 && !kernelDensity) return [];

    const density = kernelDensity || calculateKernelDensity(data, 200);
    const { x, y } = density;

    return x.map((val, index) => ({
      asset: val,
      probability: y[index] * 100000, // パーセンテージに変換 (scaling factor from original code)
    }));
  }, [data, kernelDensity]);

  if (chartData.length === 0) return null;

  return (
    <div style={{ width: "100%", height: "100%", minHeight: "300px" }}>
      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="asset"
            tickFormatter={(value) => Math.floor(value / 10000).toLocaleString()}
            label={{
              value: t("common.statistics.finalAsset") + " (万円)",
              position: "insideBottomRight",
              offset: -5,
            }}
          />
          <YAxis
            tickFormatter={(value) => `${value.toFixed(1)}%`}
            label={{
              value: t("common.statistics.probability"),
              angle: -90,
              position: "insideLeft",
            }}
          />
          <Tooltip
            formatter={(value: number) => [`${value.toFixed(2)}%`, t("common.statistics.probability")]}
            labelFormatter={(label: number) => `¥${Math.floor(label).toLocaleString()}`}
          />
          <Area
            type="monotone"
            dataKey="probability"
            stroke="#3b82f6"
            fill="rgba(59, 130, 246, 0.2)"
            strokeWidth={2}
          />
          {targetAmount && targetAmount > 0 && (
            <ReferenceLine
              x={targetAmount}
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="3 3"
              label={{
                value: t("riskSimulation.targetAmount"),
                position: "top",
                fill: "#f59e0b",
                fontSize: 12,
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
